import range from 'lodash.range';
import i18n from '../../../i18n';
import {
  ApplicationDataFields,
  CohortDataFields,
  DataFieldWithDataType,
  DataTypes,
  DataTypesAttributes,
  EmployeeDataFields,
  EvaluationDataFields,
  JobDataFields,
  JoinersViewDataFields,
  PayrollDataFields,
  Versioning,
} from './types';

export const MINIMUM_SEARCH_INPUT = 2;

export const ASCII_ARROW = '\u21D2';

export const DELTA_PERCENTAGE = 'deltaPercentage';
export const DEFAULT_CURRENCY = 'JPY';
export const CONFIDENTIAL_VALUE = 'CONFIDENTIAL';

export const UNDEFINED_DISPLAY_KEY = 'common:commonValues.missingValue';
export const CONFIDENTIAL_DISPLAY_KEY = 'common:commonValues.confidential';
export const DATA_NOT_AVAILABLE_VALUE = '-';

export const DEFAULT_OPTION = 'default';
export const $HierarchicalFilterSeparator$ = '$HierarchicalFilterSeparator$';
export const $UndefinedValueIndicator$ = '$UndefinedValueIndicator$';
export const UndefinedDisplayKey = 'common:commonValues.missingValue';
export const UndefinedValue = `${$UndefinedValueIndicator$}${i18n.t(UndefinedDisplayKey)}`;

export const LAST_ROUTE_KEY = 'lastRoute';
export const SELECTED_LANGUAGE_KEY = 'i18nextLng';
export const CURRENT_USER_KEY = 'CURRENT_USER';
export const DOMAIN_KEY = 'DOMAIN';
export const SIMULATED_ROLE_KEY = 'SIMULATED_ROLE';
export const EXECUTOR_ROLE_KEY = 'EXECUTOR_ROLE';
export const EXPORT_REF_ID = 'dashboardRoot';
export const FAVORITE_DASHBOARDS_KEY = 'FAVORITE_DASHBOARDS';

export enum ROUTE_TYPES {
  LANDING = 'LANDING',
  PUBLIC = 'PUBLIC',
  DASHBOARD = 'DASHBOARD',
  ADMIN = 'ADMIN',
  PANALYT_SUPER_ADMIN = 'PANALYTSUPERADMIN',
}

class RouteUrl {
  constructor(
    readonly routeType: ROUTE_TYPES,
    readonly routePath: string
  ) {}

  getFullPath = () => `${routeTypeToPath[this.routeType]}/${this.routePath}`;
  getType = () => this.routeType;
}

const routeTypeToPath = {
  [ROUTE_TYPES.LANDING]: '',
  [ROUTE_TYPES.PUBLIC]: '',
  [ROUTE_TYPES.ADMIN]: '/admin',
  [ROUTE_TYPES.PANALYT_SUPER_ADMIN]: '/panalyt-superadmin',
  [ROUTE_TYPES.DASHBOARD]: '/dashboard',
};

export const ROUTE_URLS = {
  MY_PANALYT: new RouteUrl(ROUTE_TYPES.LANDING, 'my-panalyt'),
  ALIAS: new RouteUrl(ROUTE_TYPES.ADMIN, 'alias'),
  DATA_UPLOAD: new RouteUrl(ROUTE_TYPES.ADMIN, 'data-upload'),
  EDIT_ROLE_PERMISSIONS: new RouteUrl(
    ROUTE_TYPES.ADMIN,
    'edit-role-permissions'
  ),
  MY_ACCOUNT: new RouteUrl(ROUTE_TYPES.ADMIN, 'my-account'),
  PERMISSIONS: new RouteUrl(ROUTE_TYPES.ADMIN, 'permissions'),
  VIEW_ROLE_PERMISSIONS: new RouteUrl(
    ROUTE_TYPES.ADMIN,
    'view-role-permissions'
  ),
  ATTRITION: new RouteUrl(ROUTE_TYPES.DASHBOARD, 'attrition'),
  DIVERSITY_GENDER: new RouteUrl(ROUTE_TYPES.DASHBOARD, 'diversity-gender'),
  EVALUATION: new RouteUrl(ROUTE_TYPES.DASHBOARD, 'evaluation'),
  JOINERS_AND_LEAVERS: new RouteUrl(ROUTE_TYPES.DASHBOARD, 'joiners-leavers'),
  MEDIAN_BASE: new RouteUrl(ROUTE_TYPES.DASHBOARD, 'median-base'),
  MOVEMENT: new RouteUrl(ROUTE_TYPES.DASHBOARD, 'movement'),
  PAYROLL: new RouteUrl(ROUTE_TYPES.DASHBOARD, 'payroll'),
  PEOPLE_BALANCE_SHEET: new RouteUrl(
    ROUTE_TYPES.DASHBOARD,
    'people-balance-sheet'
  ),
  PEOPLE_SNAPSHOT: new RouteUrl(ROUTE_TYPES.DASHBOARD, 'people-snapshot'),
  RECRUITMENT: new RouteUrl(ROUTE_TYPES.DASHBOARD, 'recruitment'),
  RECRUITMENT_BALANCE_SHEET: new RouteUrl(
    ROUTE_TYPES.DASHBOARD,
    'recruitment-balance-sheet'
  ),
  SURVEY: new RouteUrl(ROUTE_TYPES.DASHBOARD, 'survey'),
  WORK_HOURS_AND_OT: new RouteUrl(ROUTE_TYPES.DASHBOARD, 'work-hours-and-ot'),
  BACKEND_API: new RouteUrl(ROUTE_TYPES.PANALYT_SUPER_ADMIN, 'backend-api'),
  BATCH_ACTIONS: new RouteUrl(ROUTE_TYPES.PANALYT_SUPER_ADMIN, 'batch-actions'),
  COMPANY_DASHBOARDS: new RouteUrl(
    ROUTE_TYPES.PANALYT_SUPER_ADMIN,
    'company-dashboards'
  ),
  COMPANY_LIST: new RouteUrl(ROUTE_TYPES.PANALYT_SUPER_ADMIN, 'company-list'),
  AUDIT_LOGS: new RouteUrl(ROUTE_TYPES.PANALYT_SUPER_ADMIN, 'audit-logs'),
  DOMAIN_PREFERENCES: new RouteUrl(
    ROUTE_TYPES.PANALYT_SUPER_ADMIN,
    'manage-company/domain-preferences'
  ),
  MANAGE_DOMAIN_PREFERENCES: new RouteUrl(
    ROUTE_TYPES.PANALYT_SUPER_ADMIN,
    'manage-domain-preferences'
  ),
  MANAGE_DOMAIN_SETTINGS: new RouteUrl(
    ROUTE_TYPES.PANALYT_SUPER_ADMIN,
    'manage-domain-settings'
  ),
  MANAGE_METRICS: new RouteUrl(
    ROUTE_TYPES.PANALYT_SUPER_ADMIN,
    'manage-metrics'
  ),
  MULTI_DOMAIN_AUTH: new RouteUrl(
    ROUTE_TYPES.PANALYT_SUPER_ADMIN,
    'multi-domain-auth'
  ),
  CHAPPI_CONVERSATIONS: new RouteUrl(
    ROUTE_TYPES.PANALYT_SUPER_ADMIN,
    'chappi-conversations'
  ),
  FORGOT_PASSWORD: new RouteUrl(ROUTE_TYPES.PUBLIC, 'forgotpassword'),
  RESET_PASSWORD: new RouteUrl(ROUTE_TYPES.PUBLIC, 'reset'),
  LOGIN: new RouteUrl(ROUTE_TYPES.PUBLIC, 'login'),
  LOGIN_ERROR: new RouteUrl(ROUTE_TYPES.PUBLIC, 'login-error'),
  NO_ACCOUNT_ERROR: new RouteUrl(ROUTE_TYPES.PUBLIC, 'no-account-error'),
  SETUP_ERROR: new RouteUrl(ROUTE_TYPES.PUBLIC, 'setup-error'),
  WRONG_AUTH_METHOD_ERROR: new RouteUrl(
    ROUTE_TYPES.PUBLIC,
    'wrong-auth-method-error'
  ),
  SOMETHING_WENT_WRONG: new RouteUrl(
    ROUTE_TYPES.PUBLIC,
    'something-went-wrong'
  ),
};

export const DEFAULT_LANDING_PAGE = ROUTE_URLS.MY_PANALYT.getFullPath();

export enum PartnerDomains {
  CREXTA = 'crexta',
}

export enum Domains {
  AU_FINANCIAL_HOLDINGS = 'au_financial_holdings',
  BRIDGESTONE = 'bridgestone',
  CISNE = 'cisne',
  TD_HOLDINGS = 'td_holdings',
  CREATURES = 'creatures',
  IVS2023 = 'ivs2023',
  I_PEX = 'i_pex',
  JT = 'jt',
  JTB = 'jtb',
  J_OILMILLS = 'j_oilmills',
  JX_METALS = 'jx_metals',
  KHNEOCHEM = 'khneochem',
  LOOOP = 'looop',
  MIXI_COMPANY_WIDE = 'mixi_company_wide',
  MSAD = 'irric',
  NECNSI = 'necnsi',
  NIKKI = 'nikki',
  NSSOL = 'nssol',
  PATAGONIA = 'patagonia',
  PERSOLAPAC = 'persolapac',
  SHIRATORI = 'shiratori',
  SHIZUOKAGAS = 'shizuokagas',
  SWAN = 'swan',
  SWAN_ASIA = 'swan.asia',
  SHIP_HEALTHCARE = 'ship_healthcare',
  WASHI = 'washi',
}

export const JapaneseEnterpriseClientDomains = [
  Domains.AU_FINANCIAL_HOLDINGS,
  Domains.BRIDGESTONE,
  Domains.CREATURES,
  Domains.JT,
  Domains.JTB,
  Domains.J_OILMILLS,
  Domains.JX_METALS,
  Domains.KHNEOCHEM,
  Domains.LOOOP,
  Domains.MIXI_COMPANY_WIDE,
  Domains.MSAD,
  Domains.NECNSI,
  Domains.NIKKI,
  Domains.NSSOL,
  Domains.PATAGONIA,
  Domains.PERSOLAPAC,
  Domains.SHIZUOKAGAS,
  Domains.SHIP_HEALTHCARE,
];

export enum Languages {
  JA = 'ja',
  EN = 'en',
}

export enum ReservedRoleNames {
  SUPERADMIN = 'SUPERADMIN',
  ADMIN = 'ADMIN',
  GLOBAL_ACCESS = 'Global Access',
}

export const HierarchicalFields: Set<DataFieldWithDataType> = new Set([
  { dataType: DataTypes.EMPLOYEE, dataField: EmployeeDataFields.LOCATION },
  { dataType: DataTypes.EMPLOYEE, dataField: EmployeeDataFields.ORGANIZATION },
  {
    dataType: DataTypes.EMPLOYEE,
    dataField: EmployeeDataFields.EMPLOYMENT_TYPE,
  },
  { dataType: DataTypes.EMPLOYEE, dataField: EmployeeDataFields.JOB_GRADE },
  { dataType: DataTypes.EMPLOYEE, dataField: EmployeeDataFields.FUNCTION },
  { dataType: DataTypes.EMPLOYEE, dataField: EmployeeDataFields.POSITION },
  {
    dataType: DataTypes.EMPLOYEE,
    dataField: EmployeeDataFields.RECRUITMENT_CATEGORY_HIERARCHICAL,
  },
  {
    dataType: DataTypes.EMPLOYEE,
    dataField: EmployeeDataFields.NATIONALITY_HIERARCHICAL,
  },
  {
    dataType: DataTypes.EMPLOYEE,
    dataField: EmployeeDataFields.INTERNAL_JOB_GRADE,
  },
  {
    dataType: DataTypes.EMPLOYEE,
    dataField: EmployeeDataFields.INTERNAL_JOB_TITLE,
  },
  ...range(1, 10).map((num) => {
    return {
      dataType: DataTypes.EMPLOYEE,
      dataField: `CUSTOM_FIELD_${num}` as EmployeeDataFields,
    };
  }),
  {
    dataType: DataTypes.JOINERS_VIEW,
    dataField: JoinersViewDataFields.LOCATION,
  },
  {
    dataType: DataTypes.JOINERS_VIEW,
    dataField: JoinersViewDataFields.ORGANIZATION,
  },
  {
    dataType: DataTypes.JOINERS_VIEW,
    dataField: JoinersViewDataFields.EMPLOYMENT_TYPE,
  },
  {
    dataType: DataTypes.JOINERS_VIEW,
    dataField: JoinersViewDataFields.JOB_GRADE,
  },
  {
    dataType: DataTypes.JOINERS_VIEW,
    dataField: JoinersViewDataFields.FUNCTION,
  },
  {
    dataType: DataTypes.JOINERS_VIEW,
    dataField: JoinersViewDataFields.POSITION,
  },
  {
    dataType: DataTypes.JOINERS_VIEW,
    dataField: JoinersViewDataFields.RECRUITMENT_CATEGORY_HIERARCHICAL,
  },
  {
    dataType: DataTypes.JOINERS_VIEW,
    dataField: JoinersViewDataFields.NATIONALITY_HIERARCHICAL,
  },
  {
    dataType: DataTypes.JOINERS_VIEW,
    dataField: JoinersViewDataFields.INTERNAL_JOB_GRADE,
  },
  {
    dataType: DataTypes.JOINERS_VIEW,
    dataField: JoinersViewDataFields.INTERNAL_JOB_TITLE,
  },
  ...range(1, 10).map((num) => {
    return {
      dataType: DataTypes.JOINERS_VIEW,
      dataField: `CUSTOM_FIELD_${num}` as JoinersViewDataFields,
    };
  }),
  { dataType: DataTypes.JOB, dataField: JobDataFields.ORGANIZATION },
  { dataType: DataTypes.JOB, dataField: JobDataFields.LOCATION },
  { dataType: DataTypes.JOB, dataField: JobDataFields.JOB_NAME },
  { dataType: DataTypes.JOB, dataField: JobDataFields.OFFICE },
  {
    dataType: DataTypes.JOB,
    dataField: JobDataFields.RECRUITMENT_CATEGORY_HIERARCHICAL,
  },
  { dataType: DataTypes.JOB, dataField: JobDataFields.CUSTOM_FIELD_1 },
  { dataType: DataTypes.JOB, dataField: JobDataFields.CUSTOM_FIELD_2 },
  { dataType: DataTypes.JOB, dataField: JobDataFields.JOB_GRADE },
  { dataType: DataTypes.JOB, dataField: JobDataFields.FUNCTION },
  { dataType: DataTypes.APPLICATION, dataField: ApplicationDataFields.SOURCE },
  {
    dataType: DataTypes.APPLICATION,
    dataField: ApplicationDataFields.REJECTION_REASON,
  },
  {
    dataType: DataTypes.APPLICATION,
    dataField: ApplicationDataFields.APPLICATION_CURRENT_STAGE,
  },
  {
    dataType: DataTypes.APPLICATION,
    dataField: ApplicationDataFields.CUSTOM_FIELD_1,
  },
]);

export const DataTypesWithAttributes: Record<DataTypes, DataTypesAttributes> = {
  [DataTypes.MANAGERENPS]: {
    versioning: Versioning.MONTHLY,
    defaultIdColumn: EmployeeDataFields.EMPLOYEE_ID,
  },
  [DataTypes.EMPLOYEE]: {
    versioning: Versioning.MONTHLY,
    defaultIdColumn: EmployeeDataFields.EMPLOYEE_ID,
  },
  [DataTypes.APPLICATION]: {
    versioning: Versioning.MONTHLY,
    defaultIdColumn: ApplicationDataFields.APPLICATION_ID,
  },
  [DataTypes.RECRUITERJOBS]: {
    versioning: Versioning.MONTHLY,
    defaultIdColumn: ApplicationDataFields.APPLICATION_ID,
  }, // TODO: fix this
  [DataTypes.JOB]: {
    versioning: Versioning.MONTHLY,
    defaultIdColumn: ApplicationDataFields.APPLICATION_ID,
  }, // TODO: fix this
  [DataTypes.OFFER]: {
    versioning: Versioning.MONTHLY,
    defaultIdColumn: ApplicationDataFields.APPLICATION_ID,
  }, // TODO: fix this
  [DataTypes.QUESTIONANSWER]: {
    versioning: Versioning.MONTHLY,
    defaultIdColumn: EmployeeDataFields.EMPLOYEE_ID,
  }, // TODO: fix this
  [DataTypes.TIMEANDATTENDANCEMONTHLY]: {
    versioning: Versioning.MONTHLY,
    defaultIdColumn: EmployeeDataFields.EMPLOYEE_ID,
  }, // TODO: fix this
  [DataTypes.PAYROLL]: {
    versioning: Versioning.MONTHLY,
    defaultIdColumn: EmployeeDataFields.EMPLOYEE_ID,
  }, // TODO: fix this
  [DataTypes.EVALUATION]: {
    versioning: Versioning.NONE,
    defaultIdColumn: EmployeeDataFields.EMPLOYEE_ID,
  }, // TODO: fix this
  [DataTypes.JOINERS_VIEW]: {
    versioning: Versioning.MONTHLY,
    defaultIdColumn: EmployeeDataFields.EMPLOYEE_ID,
  }, // TODO: fix this
  [DataTypes.FINANCIALMETRICS]: {
    versioning: Versioning.MONTHLY,
    defaultIdColumn: EmployeeDataFields.EMPLOYEE_ID,
  }, // TODO: fix this
  [DataTypes.INTERVIEW]: {
    versioning: Versioning.MONTHLY,
    defaultIdColumn: EmployeeDataFields.EMPLOYEE_ID,
  }, // TODO: fix this
  [DataTypes.COHORT]: {
    versioning: Versioning.NONE,
    defaultIdColumn: CohortDataFields.COHORT_VALUE,
  },
};

const payrollSalaryFields = [
  {
    dataType: DataTypes.PAYROLL,
    dataField: PayrollDataFields.CONTRACTUAL_BASE_SALARY,
  },
  {
    dataType: DataTypes.PAYROLL,
    dataField: PayrollDataFields.STANDARDIZED_CONTRACTUAL_BASE_SALARY,
  },
  {
    dataType: DataTypes.PAYROLL,
    dataField: PayrollDataFields.ACTUAL_BASE_SALARY,
  },
  {
    dataType: DataTypes.PAYROLL,
    dataField: PayrollDataFields.STANDARDIZED_ACTUAL_BASE_SALARY,
  },
  {
    dataType: DataTypes.PAYROLL,
    dataField: PayrollDataFields.STATUTORY_PAYMENT_TOTAL,
  },
  {
    dataType: DataTypes.PAYROLL,
    dataField: PayrollDataFields.STANDARDIZED_STATUTORY_PAYMENT_TOTAL,
  },
  {
    dataType: DataTypes.PAYROLL,
    dataField: PayrollDataFields.NON_STATUTORY_PAYMENT_TOTAL,
  },
  {
    dataType: DataTypes.PAYROLL,
    dataField: PayrollDataFields.STANDARDIZED_NON_STATUTORY_PAYMENT_TOTAL,
  },
  { dataType: DataTypes.PAYROLL, dataField: PayrollDataFields.TOTAL_BONUS },
  {
    dataType: DataTypes.PAYROLL,
    dataField: PayrollDataFields.STANDARDIZED_TOTAL_BONUS,
  },
  { dataType: DataTypes.PAYROLL, dataField: PayrollDataFields.TOTAL_ALLOWANCE },
  {
    dataType: DataTypes.PAYROLL,
    dataField: PayrollDataFields.STANDARDIZED_TOTAL_ALLOWANCE,
  },
  {
    dataType: DataTypes.PAYROLL,
    dataField: PayrollDataFields.TOTAL_PAYROLL_COST,
  },
  {
    dataType: DataTypes.PAYROLL,
    dataField: PayrollDataFields.TOTAL_BOARD_COMPENSATION,
  },
  {
    dataType: DataTypes.PAYROLL,
    dataField: PayrollDataFields.TOTAL_EMPLOYEE_AWARD,
  },
  {
    dataType: DataTypes.PAYROLL,
    dataField: PayrollDataFields.TOTAL_REIMBURSEMENT,
  },
  {
    dataType: DataTypes.PAYROLL,
    dataField: PayrollDataFields.STANDARDIZED_TOTAL_PAYROLL_COST,
  },
  {
    dataType: DataTypes.PAYROLL,
    dataField: PayrollDataFields.STANDARDIZED_TOTAL_BOARD_COMPENSATION,
  },
  {
    dataType: DataTypes.PAYROLL,
    dataField: PayrollDataFields.STANDARDIZED_TOTAL_EMPLOYEE_AWARD,
  },
  {
    dataType: DataTypes.PAYROLL,
    dataField: PayrollDataFields.STANDARDIZED_TOTAL_REIMBURSEMENT,
  },
  {
    dataType: DataTypes.PAYROLL,
    dataField: PayrollDataFields.TOTAL_CASH_COMPENSATION,
  },
];

export const standardizedSalaryFields = [
  {
    dataType: DataTypes.EMPLOYEE,
    dataField: EmployeeDataFields.STANDARDIZED_BASE_SALARY,
  },
  {
    dataType: DataTypes.EMPLOYEE,
    dataField: EmployeeDataFields.STANDARDIZED_BASE_SALARY_PER_YEAR,
  },
];

export const localSalaryFields = [
  { dataType: DataTypes.EMPLOYEE, dataField: EmployeeDataFields.BASE_SALARY },
  {
    dataType: DataTypes.EMPLOYEE,
    dataField: EmployeeDataFields.REVENUE_SALARY,
  },
  {
    dataType: DataTypes.EMPLOYEE,
    dataField: EmployeeDataFields.LOCAL_REVENUE_SALARY,
  },
  {
    dataType: DataTypes.EMPLOYEE,
    dataField: EmployeeDataFields.ANNUAL_CASH_BONUS_1,
  },
  { dataType: DataTypes.EMPLOYEE, dataField: EmployeeDataFields.LOCAL_SALARY },
  {
    dataType: DataTypes.EMPLOYEE,
    dataField: EmployeeDataFields.BASE_SALARY_PER_YEAR,
  },
  {
    dataType: DataTypes.EMPLOYEE,
    dataField: EmployeeDataFields.TOTAL_COMPENSATION,
  },
  ...payrollSalaryFields,
];

export const payrollFields = [
  {
    dataType: DataTypes.PAYROLL,
    dataField: PayrollDataFields.ACTUAL_BASE_SALARY_CURRENCY,
  },
  {
    dataType: DataTypes.PAYROLL,
    dataField: PayrollDataFields.CONTRACTUAL_BASE_SALARY_CURRENCY,
  },
  {
    dataType: DataTypes.PAYROLL,
    dataField: PayrollDataFields.NON_STATUTORY_PAYMENT_TOTAL_CURRENCY,
  },
  {
    dataType: DataTypes.PAYROLL,
    dataField: PayrollDataFields.STATUTORY_PAYMENT_TOTAL_CURRENCY,
  },
  {
    dataType: DataTypes.PAYROLL,
    dataField: PayrollDataFields.STANDARDIZED_TRAVEL_AND_COMMUTING,
  },
  {
    dataType: DataTypes.PAYROLL,
    dataField: PayrollDataFields.TRAVEL_AND_COMMUTING_CURRENCY,
  },
  {
    dataType: DataTypes.PAYROLL,
    dataField: PayrollDataFields.TRAVEL_AND_COMMUTING,
  },
  {
    dataType: DataTypes.PAYROLL,
    dataField: PayrollDataFields.STANDARDIZED_OVERTIME,
  },
  {
    dataType: DataTypes.PAYROLL,
    dataField: PayrollDataFields.OVERTIME_CURRENCY,
  },
  { dataType: DataTypes.PAYROLL, dataField: PayrollDataFields.OVERTIME },
  {
    dataType: DataTypes.PAYROLL,
    dataField: PayrollDataFields.TOTAL_ALLOWANCE_CURRENCY,
  },
  {
    dataType: DataTypes.PAYROLL,
    dataField: PayrollDataFields.TOTAL_BONUS_CURRENCY,
  },
  {
    dataType: DataTypes.PAYROLL,
    dataField: PayrollDataFields.TOTAL_BOARD_COMPENSATION_CURRENCY,
  },
  {
    dataType: DataTypes.PAYROLL,
    dataField: PayrollDataFields.TOTAL_EMPLOYEE_AWARD_CURRENCY,
  },
  {
    dataType: DataTypes.PAYROLL,
    dataField: PayrollDataFields.TOTAL_REIMBURSEMENT_CURRENCY,
  },
  {
    dataType: DataTypes.PAYROLL,
    dataField: PayrollDataFields.TOTAL_PAYROLL_COST_CURRENCY,
  },
  ...payrollSalaryFields,
];

export const hrisCompFields = [
  { dataType: DataTypes.EMPLOYEE, dataField: EmployeeDataFields.BASE_SALARY },
  {
    dataType: DataTypes.EMPLOYEE,
    dataField: EmployeeDataFields.BASE_SALARY_CURRENCY,
  },
  {
    dataType: DataTypes.EMPLOYEE,
    dataField: EmployeeDataFields.REVENUE_SALARY,
  },
  {
    dataType: DataTypes.EMPLOYEE,
    dataField: EmployeeDataFields.REVENUE_SALARY_CURRENCY,
  },
  {
    dataType: DataTypes.EMPLOYEE,
    dataField: EmployeeDataFields.LOCAL_REVENUE_SALARY,
  },
  {
    dataType: DataTypes.EMPLOYEE,
    dataField: EmployeeDataFields.ANNUAL_CASH_BONUS_1,
  },
  { dataType: DataTypes.EMPLOYEE, dataField: EmployeeDataFields.LOCAL_SALARY },
  {
    dataType: DataTypes.EMPLOYEE,
    dataField: EmployeeDataFields.LOCAL_SALARY_CURRENCY,
  },
  {
    dataType: DataTypes.EMPLOYEE,
    dataField: EmployeeDataFields.TOTAL_COMPENSATION,
  },
  {
    dataType: DataTypes.EMPLOYEE,
    dataField: EmployeeDataFields.STANDARDIZED_BASE_SALARY,
  },
  {
    dataType: DataTypes.EMPLOYEE,
    dataField: EmployeeDataFields.BASE_SALARY_PER_YEAR,
  },
  {
    dataType: DataTypes.EMPLOYEE,
    dataField: EmployeeDataFields.STANDARDIZED_BASE_SALARY_PER_YEAR,
  },
  {
    dataType: DataTypes.EMPLOYEE,
    dataField: EmployeeDataFields.TOTAL_COMPENSATION_CURRENCY,
  },
  { dataType: DataTypes.EMPLOYEE, dataField: EmployeeDataFields.ICR },
];

export const employeeIdDataField = {
  dataType: DataTypes.EMPLOYEE,
  dataField: EmployeeDataFields.EMPLOYEE_ID,
};

export const startDateField = {
  dataType: DataTypes.EMPLOYEE,
  dataField: EmployeeDataFields.START_DATE,
};

export const startCalendarYearField = {
  dataType: DataTypes.EMPLOYEE,
  dataField: EmployeeDataFields.START_CALENDAR_YEAR,
};

export const startFinancialYearField = {
  dataType: DataTypes.EMPLOYEE,
  dataField: EmployeeDataFields.START_FINANCIAL_YEAR,
};

export const employmentTemporalityField = {
  dataType: DataTypes.EMPLOYEE,
  dataField: EmployeeDataFields.EMPLOYMENT_TEMPORALITY,
};

export const termDateNormalizedField = {
  dataType: DataTypes.EMPLOYEE,
  dataField: EmployeeDataFields.TERM_DATE_NORMALIZED,
};

export const evaluationCycleTypeField = {
  dataType: DataTypes.EVALUATION,
  dataField: EvaluationDataFields.EVALUATION_CYCLE_TYPE,
};

export const managerField = {
  dataType: DataTypes.EMPLOYEE,
  dataField: EmployeeDataFields.MANAGER_ID,
};
